import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { formatPrice } from '@/data/money';
import { questionGrade } from '@/data/scoring';
import AccuracyMeter from '@/components/AccuracyMeter';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import ErrorState from '@/components/ErrorState';
import { ArrowLeft, CheckCircle2, MessageCircle, Star } from 'lucide-react';
import { toast } from 'sonner';
import { divinationLabel } from '@/data/divination';
import { reqNo } from '@/data/format';
import { birthDateText, birthTimeText } from '@/data/birth';

const RequestDetail = () => {
  const { requestId } = useParams();
  const { user, config, rates, displayCurrency } = useAuth();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [truthInputs, setTruthInputs] = useState({});
  const [savingTruths, setSavingTruths] = useState(false);
  const [editingTruths, setEditingTruths] = useState(false);

  useEffect(() => {
    fetchData();
    // Auto-refresh so new astrologer answers appear without a manual reload.
    // Background ticks fail silently — no red-toast loop on a flaky connection.
    const id = setInterval(() => fetchData(true), 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const fetchData = async (silent = false) => {
    try {
      const [reqRes, ansRes, ordersRes] = await Promise.all([
        axios.get(`${API}/requests/${requestId}`, { withCredentials: true }),
        axios.get(`${API}/requests/${requestId}/answers`, { withCredentials: true }),
        axios.get(`${API}/orders`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setRequest(reqRes.data);
      setAnswers(ansRes.data);
      setMyOrders((ordersRes.data || []).filter(o => o.request_id === requestId));
    } catch (error) {
      console.error('Error fetching data:', error);
      if (!silent) toast.error('無法載入資料');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg">載入中...</p>
      </div>
    );
  }

  if (!request) {
    return <ErrorState message="找不到求測單" />;
  }

  const orderFor = (astrologerId) => myOrders.find((o) => o.astrologer_id === astrologerId);
  const maxPicks = config?.max_picks_per_request || 1;
  const postPickDays = config?.post_pick_days || 7;
  const free = config?.free_mode;
  const isFull = myOrders.length >= maxPicks;

  // Backfill the querent's own known answers (truths) -> backend scores every answer.
  const submitTruths = async () => {
    const qs = request.verification_questions || [];
    const myTruths = request.my_truths || {};
    const truths = {};
    for (const q of qs) {
      // In edit mode every question is re-submitted; otherwise locked ones are skipped.
      if (!editingTruths && myTruths[String(q.order)]) continue;
      const t = (truthInputs[q.order] || '').trim();
      if (!t) { toast.error('真相要一次過填曬所有問題'); return; }
      truths[q.order] = t;
    }
    if (!Object.keys(truths).length) return;
    setSavingTruths(true);
    try {
      await axios.put(`${API}/requests/${requestId}/verification-truths`, { truths }, { withCredentials: true });
      setTruthInputs({});
      setEditingTruths(false);
      await fetchData();
      toast.success(answers.length > 0 ? '真相已儲存，評分完成後會更新' : '真相已儲存');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '提交失敗');
    } finally {
      setSavingTruths(false);
    }
  };


  const toggleStar = async (answer) => {
    const next = !answer.starred;
    setAnswers((prev) => prev.map((a) => (a.answer_id === answer.answer_id ? { ...a, starred: next } : a)));
    try {
      await axios.post(`${API}/requests/${requestId}/star`, { astrologer_id: answer.astrologer_id, starred: next }, { withCredentials: true });
    } catch (e) {
      setAnswers((prev) => prev.map((a) => (a.answer_id === answer.answer_id ? { ...a, starred: !next } : a)));
      toast.error('操作失敗');
    }
  };

  const StarButton = ({ answer }) => (
    <button
      type="button"
      onClick={() => toggleStar(answer)}
      className="flex-shrink-0 p-1 rounded-sm hover:bg-[#F5F0E6]"
      data-testid={`star-${answer.answer_id}`}
      aria-label={answer.starred ? '取消星標' : '星標'}
    >
      <Star className={`w-5 h-5 ${answer.starred ? 'fill-[#E8A33D] text-[#E8A33D]' : 'text-[#C9C2B5]'}`} strokeWidth={1.5} />
    </button>
  );

  const sortedAnswers = [...answers].sort((a, b) => (b.starred ? 1 : 0) - (a.starred ? 1 : 0));

  const finishRequest = async () => {
    try {
      await axios.post(`${API}/requests/${requestId}/finish`, {}, { withCredentials: true });
      toast.success('已完成此求測單');
      fetchData();
    } catch (e) {
      toast.error('操作失敗');
    }
  };

  const renderActionButton = () => {
    // CompareAnswers renders fine with a single answer — with one it's "view & pick",
    // with two+ it's a real comparison. (Matches the dashboard's 查看並選擇命理師 CTA.)
    if (answers.length >= 1) {
      return (
        <Link to={`/client/compare/${requestId}`}>
          <Button
            className="bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm shadow-sm"
            data-testid="compare-answers-btn"
          >
            <CheckCircle2 className="mr-2" strokeWidth={1.5} />
            {answers.length >= 2 ? '比較答案' : '查看答案並選擇'}
          </Button>
        </Link>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-[#E5E0D8] px-6 md:px-12 lg:px-24 py-4">
        <Link to="/client/dashboard">
          <h2 className="text-xl font-serif text-[#C93B2B]">命理配對平台</h2>
        </Link>
      </nav>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <Link to="/client/dashboard">
          <Button variant="ghost" className="mb-6 rounded-sm" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            返回
          </Button>
        </Link>

        <div className="max-w-5xl mx-auto">
          <div className="bg-white border border-[#E5E0D8] rounded-sm p-6 md:p-8 mb-8">
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center border border-[#C93B2B] text-[#C93B2B] px-2 py-1 text-xs rounded-none font-serif tracking-widest">
                  {divinationLabel(request)}
                </div>
                {request.seq && <span className="text-xs text-[#9C9C9C] font-mono">{reqNo(request.seq)}</span>}
              </div>
              <span className={`inline-flex items-center px-2 py-1 text-xs rounded-sm ${
                request.expired ? 'bg-[#E5E0D8] text-[#9A9A9A]'
                  : (request.status === 'open' && myOrders.length >= 1) ? 'bg-[#FBE8D8] text-[#C9772B]'
                  : request.status === 'open' ? 'bg-[#F5F0E6] text-[#C93B2B]' : 'bg-[#E5E0D8] text-[#1A1A1A]'
              }`}>
                {request.expired ? '已過期'
                  : (request.status === 'open' && myOrders.length >= 1) ? `已選 ${myOrders.length} 位（可再選 ${maxPicks - myOrders.length} 位）`
                  : request.status === 'open' ? '開放中' : '已完成'}
              </span>
            </div>

            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-serif text-[#1A1A1A]">求測單詳情</h1>
              {request.status === 'open' && !request.expired && request.days_left != null && (
                <p className="text-sm text-[#9A8A6A] mt-1" data-testid="days-left">
                  有效 {request.days_left} 日 · 到期未有選擇會自動關閉
                </p>
              )}
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-[#5C5C5C]">姓名</p>
              <p className="text-lg font-medium text-[#1A1A1A]">{request.client_name}</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <p className="text-xs text-[#5C5C5C] mb-1">出生日期</p>
                <p className="text-sm font-medium text-[#1A1A1A]">{birthDateText(request)}</p>
              </div>
              <div>
                <p className="text-xs text-[#5C5C5C] mb-1">出生時間</p>
                <p className="text-sm font-medium text-[#1A1A1A]">{birthTimeText(request)}</p>
              </div>
              <div>
                <p className="text-xs text-[#5C5C5C] mb-1">性別</p>
                <p className="text-sm font-medium text-[#1A1A1A]">{request.gender === 'male' ? '男' : '女'}</p>
              </div>
              <div>
                <p className="text-xs text-[#5C5C5C] mb-1">出生地區</p>
                <p className="text-sm font-medium text-[#1A1A1A]">{request.region}</p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-serif text-[#1A1A1A] mb-3">驗證問題</h3>
              <div className="space-y-3">
                {request.verification_questions.map((q) => (
                  <div key={`q-${q.order}`} className="bg-[#F5F0E6] rounded-sm p-4">
                    <p className="text-xs text-[#C93B2B] font-bold mb-1">問題 {q.order}</p>
                    <p className="text-sm text-[#1A1A1A]">{q.question_text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl sm:text-2xl font-serif text-[#1A1A1A]">
              命理師回答 ({answers.length})
            </h2>
            {renderActionButton()}
          </div>

          {request.status === 'open' && myOrders.length >= 1 && (
            <div className="mb-6 bg-[#FBF3E8] border border-[#E8A33D] rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="finish-hint">
              <p className="text-sm text-[#1A1A1A]">
                你已選擇 {myOrders.length} 位命理師{myOrders.length < maxPicks ? `，可再選 ${maxPicks - myOrders.length} 位；${postPickDays} 日內未再選將自動完成` : ''}。亦可立即結束此求測單。
              </p>
              <Button
                onClick={finishRequest}
                variant="outline"
                className="rounded-sm border-[#C9772B] text-[#C9772B] hover:bg-[#FBF3E8] whitespace-nowrap"
                data-testid="finish-request-btn"
              >
                完成求測單
              </Button>
            </div>
          )}

          {request.expired && (
            <div className="mb-6 bg-[#F5F0E6] rounded-sm p-4" data-testid="expired-hint">
              <p className="text-sm text-[#5C5C5C]">
                此求測單已過期：命理師不會再回答{free ? '；如下方已有回答，你仍可選擇。' : '；如下方已有回答，你仍可選擇並付款。'}
              </p>
            </div>
          )}

          {request.status === 'open' && answers.length === 1 && (
            <div className="mb-6 bg-[#FEF8F0] border border-[#C93B2B] rounded-sm p-4" data-testid="single-answer-hint">
              <p className="text-sm text-[#1A1A1A]">
                目前有 1 位命理師回答。你可以直接選擇下方這位，或再等其他命理師回答後一併比較。
              </p>
            </div>
          )}

          {(() => {
            const qs = request.verification_questions || [];
            if (!qs.length) return null;
            const myTruths = request.my_truths || {};
            const unfilled = qs.filter((q) => !(String(q.order) in myTruths));
            const canEdit = answers.length === 0;   // typo escape hatch: editable until the first answer
            return (
              <div className="mb-6 bg-white border border-[#C93B2B] rounded-sm p-5" data-testid="truth-backfill">
                <p className="text-sm font-medium text-[#1A1A1A] mb-1">你的真相（評分依據）</p>
                <p className="text-xs text-[#5C5C5C] mb-3">
                  真相要<b>一次過填曬所有問題</b>（私下，<b>唔會畀命理師睇</b>）。有命理師回答之前可以修改；<b>一有回答就鎖定</b>，用嚟評命理師準確度。
                </p>
                <div className="space-y-3">
                  {qs.map((q) => {
                    const t = myTruths[String(q.order)];
                    const locked = t && !editingTruths;
                    return (
                      <div key={q.order}>
                        <p className="text-sm text-[#1A1A1A] mb-1">問題 {q.order}：{q.question_text}</p>
                        {locked ? (
                          <p className="text-sm bg-[#F5F0E6] rounded-sm p-2 text-[#1A1A1A]" data-testid={`my-truth-${q.order}`}>🔒 你填咗：{t}</p>
                        ) : (
                          <textarea
                            value={truthInputs[q.order] || ''}
                            onChange={(e) => setTruthInputs((p) => ({ ...p, [q.order]: e.target.value }))}
                            placeholder="你知道嘅真實答案（要一次過填曬所有問題）"
                            rows={2}
                            maxLength={50}
                            className="w-full rounded-sm border border-[#E5E0D8] p-2 text-sm"
                            data-testid={`truth-input-${q.order}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                {(unfilled.length > 0 || editingTruths) && (
                  <Button onClick={submitTruths} disabled={savingTruths}
                    className="mt-3 bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm" data-testid="submit-truths-btn">
                    {savingTruths ? '儲存中…' : editingTruths ? '儲存修改' : (answers.length > 0 ? '提交真相並評分' : '提交真相')}
                  </Button>
                )}
                {canEdit && unfilled.length === 0 && !editingTruths && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      const init = {};
                      qs.forEach((q) => { init[q.order] = myTruths[String(q.order)] || ''; });
                      setTruthInputs(init);
                      setEditingTruths(true);
                    }}
                    className="mt-3 rounded-sm border-[#C93B2B] text-[#C93B2B]"
                    data-testid="edit-truths-btn"
                  >
                    修改真相（有命理師回答前）
                  </Button>
                )}
              </div>
            );
          })()}

          {answers.length === 0 ? (
            <div className="bg-white border border-[#E5E0D8] rounded-sm p-12 text-center">
              <p className="text-sm text-[#5C5C5C]">尚無命理師回答，請稍後再查看</p>
            </div>
          ) : (
            <div className="space-y-6">
              {sortedAnswers.map((answer) => (
                <div key={answer.answer_id} className="bg-white border border-[#E5E0D8] rounded-sm p-6 md:p-8" data-testid={`answer-${answer.answer_id}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-serif text-[#1A1A1A] mb-1">{answer.astrologer_name}</h3>
                      {answer.astrologer_profile && (
                        <p className="text-xs text-[#5C5C5C]">
                          {answer.astrologer_profile.specialties.join(' · ')} · {answer.astrologer_profile.price_range}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <StarButton answer={answer} />
                      <p className="text-xs text-[#5C5C5C]">
                        {new Date(answer.created_at).toLocaleDateString('zh-TW')}
                      </p>
                    </div>
                  </div>

                  <AccuracyMeter answer={answer} className="mb-3" />

                  <div className="bg-[#F5F0E6] rounded-sm p-4 mb-4 space-y-2">
                    <p className="text-xs text-[#5C5C5C]">報價</p>
                    {(answer.quotes && answer.quotes.length
                      ? answer.quotes
                      : [{ scope: '諮詢', price: answer.price, consultation_time_minutes: answer.consultation_time_minutes }]
                    ).map((q) => {
                      const f = formatPrice(q.price, answer.currency, displayCurrency, rates);
                      return (
                        <div key={q.scope} className="flex justify-between items-center">
                          <span className="text-sm text-[#1A1A1A]">{q.scope}</span>
                          <span className="text-sm text-right">
                            <span className="font-medium text-[#C93B2B]">{f.text}</span>
                            <span className="text-[#5C5C5C]"> · {q.consultation_time_minutes} 分鐘</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="space-y-4 mb-4">
                    {answer.answers.map((ans) => {
                      const g = questionGrade((answer.question_scores || []).find((x) => x.order === ans.question_order));
                      return (
                        <div key={`ans-${ans.question_order}`} className="border-l-2 border-[#C93B2B] pl-4">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-xs text-[#C93B2B] font-bold">問題 {ans.question_order}</p>
                            {g && <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${g.cls}`}>{g.label}</span>}
                          </div>
                          <p className="text-sm text-[#1A1A1A] leading-relaxed">{ans.answer_text}</p>
                        </div>
                      );
                    })}
                  </div>

                  {answer.observation && (
                    <div className="bg-[#F5F0E6] rounded-sm p-4">
                      <p className="text-xs text-[#5C5C5C] font-bold mb-1">補充觀察</p>
                      <p className="text-sm text-[#1A1A1A] leading-relaxed">{answer.observation}</p>
                    </div>
                  )}

                  <div className="mt-6 pt-4 border-t border-[#E5E0D8] flex justify-end">
                    {(() => {
                      const ord = orderFor(answer.astrologer_id);
                      if (ord) {
                        return (
                          <Link to={`/chat/${ord.order_id}`}>
                            <Button
                              variant="outline"
                              className="rounded-sm border-[#C93B2B] text-[#C93B2B] hover:bg-[#F5F0E6]"
                              data-testid={`go-chat-${answer.answer_id}`}
                            >
                              <MessageCircle className="mr-2" strokeWidth={1.5} />
                              前往諮詢室
                            </Button>
                          </Link>
                        );
                      }
                      if (request.status !== 'open' || request.expired) {
                        return <span className="text-xs text-[#9A9A9A] self-center">{request.expired ? '求測單已過期' : '求測單已完成'}</span>;
                      }
                      if (isFull) {
                        return <span className="text-xs text-[#9A9A9A] self-center">已選滿 {maxPicks} 位命理師</span>;
                      }
                      return (
                        <Button
                          onClick={() => navigate(`/client/payment/${requestId}/${answer.astrologer_id}`)}
                          className="bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm shadow-sm"
                          data-testid={`select-astrologer-${answer.answer_id}`}
                        >
                          <CheckCircle2 className="mr-2" strokeWidth={1.5} />
                          選擇此命理師
                        </Button>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestDetail;
